
import { defineComponent } from "vue";

import AcademicYearService from "@/api/Configuration/AcademicYearService";
import HeadquartersService from "@/api/Configuration/HeadquartersService";
import CourseGroupsService from "@/api/Aula/CourseGroupsService";
import CoursesService from "@/api/Aula/CoursesSevice";
import PeriodsService from "@/api/Configuration/PeriodsService";
import IndividualEnrollmentService from "@/api/Enrollment/IndividualEnrollmentService";
import store from "@/store";
import { SHOW_PRESCHOOL } from "@/enums";

import PreschoolReportsService from "@/api/Reports/ReportPreschoolScoreService";

type Option = { value: any; text: string };

export default defineComponent({
    name: "PreschoolReportsCenter",
    data() {
        return {
            academicYears: [] as any[],
            headquarters: [] as any[],
            courses: [] as any[],
            courseGroupsAll: [] as any[],
            courseGroups: [] as any[],
            periods: [] as any[],

            enrollments: [] as any[],
            selectedStudentIds: [] as number[],

            previewHtml: "" as string,
            previewStudentId: 0 as number,

            filters: {
                AcademicYearId: 0,
                HeadquarterId: 0,
                CourseGroupId: 0,
                PeriodId: 0,
                StudentId: 0,
                DeliveryMode: "PRINT" as "PRINT" | "EMAIL"
            },

            emailOptions: {
                SendToStudentEmail: true,
                SendToParentsEmail: true
            },

            loading: {
                lookups: true,
                enrollments: false,
                preview: false,
                action: false
            },

            errors: {
                filters: "",
                preview: ""
            },

            fields: [
                { key: "sel", label: "", thStyle: { width: "44px" } },
                { key: "student", label: "Estudiante" },
                { key: "GroupName", label: "Grupo", class: "text-nowrap" },
                { key: "actions", label: "Acciones", class: "text-nowrap" }
            ]
        };
    },

    computed: {
        canSearch(): boolean {
            return (
                Number(this.filters.AcademicYearId) > 0 &&
                Number(this.filters.HeadquarterId) > 0 &&
                Number(this.filters.CourseGroupId) > 0 &&
                Number(this.filters.PeriodId) > 0
            );
        },

        academicYearsOptions(): Option[] {
            return (this.academicYears || []).map((r: any) => ({
                value: r.Id,
                text: `Año ${r.Year} (${String(r.StartDate).substring(0, 10)} - ${String(r.EndDate).substring(0, 10)})`
            }));
        },

        headquartersOptions(): Option[] {
            return (this.headquarters || []).map((r: any) => ({ value: r.Id, text: r.Description }));
        },

        courseGroupsOptions(): Option[] {
            return (this.courseGroups || []).map((r: any) => ({ value: r.Id, text: r.Name }));
        },

        periodOptions(): Option[] {
            return (this.periods || []).map((p: any) => ({
                value: p.Id,
                text: p.Name || p.Description || `Período ${p.Id}`
            }));
        },

        enrolledStudentsOptions(): Option[] {
            return (this.enrollments || [])
                .map((e: any) => ({ value: e.StudentId, text: `${e.StudentName}` }))
                .sort((a: any, b: any) => (a.text || "").localeCompare(b.text || ""));
        },

        effectiveSelectedIds(): number[] {
            if (Number(this.filters.StudentId) > 0) return [Number(this.filters.StudentId)];
            if (!this.selectedStudentIds.length) return (this.enrollments || []).map((e: any) => Number(e.StudentId));
            return this.selectedStudentIds.map(n => Number(n));
        },

        effectiveSelectedCount(): number {
            return this.effectiveSelectedIds.length || 0;
        },

        canRunPrint(): boolean {
            return this.canSearch && this.enrollments.length > 0 && !this.loading.action;
        },

        canRunEmail(): boolean {
            return (
                this.canSearch &&
                this.enrollments.length > 0 &&
                this.filters.DeliveryMode === "EMAIL" &&
                !this.loading.action &&
                (this.emailOptions.SendToStudentEmail || this.emailOptions.SendToParentsEmail)
            );
        }
    },

    async mounted() {
        await this.loadLookups();
    },

    methods: {
        async loadLookups() {
            try {
                this.loading.lookups = true;

                this.academicYears = await AcademicYearService.GetAcademicYears();
                this.headquarters = await HeadquartersService.GetHeadquarters();

                // Cursos solo preescolar
                this.courses = await CoursesService.GetCourses(SHOW_PRESCHOOL.No);

                // All course groups
                const allGroups = await CourseGroupsService.GetCourseGroups();
                this.courseGroupsAll = allGroups || [];
                this.courseGroups = [];

                // Periodos
                this.periods = await PeriodsService.GetPeriods?.() || [];
            } catch (e: any) {
                store.dispatch("core/popupDialog", {
                    text: `Error cargando catálogos: ${e?.response?.data?.message || e?.message}`,
                    type: "error",
                    showCancelButton: false
                });
            } finally {
                this.loading.lookups = false;
            }
        },

        onYearChanged() {
            this.filters.CourseGroupId = 0 as any;
            this.filters.StudentId = 0 as any;
            this.enrollments = [];
            this.selectedStudentIds = [];
            this.previewHtml = "";
            this.previewStudentId = 0;
            this.errors.filters = "";

            this.rebuildCourseGroups();
            this.reloadPeriods();
        },

        onHeadquarterChanged() {
            this.filters.CourseGroupId = 0 as any;
            this.filters.StudentId = 0 as any;
            this.enrollments = [];
            this.selectedStudentIds = [];
            this.previewHtml = "";
            this.previewStudentId = 0;
            this.errors.filters = "";

            this.rebuildCourseGroups();
        },

        onGroupChanged() {
            this.filters.StudentId = 0 as any;
            this.enrollments = [];
            this.selectedStudentIds = [];
            this.previewHtml = "";
            this.previewStudentId = 0;
            this.errors.filters = "";
        },

        onStudentChanged() {
            this.selectedStudentIds = [];
        },

        async reloadPeriods() {
            try {
                const yearId = Number(this.filters.AcademicYearId || 0);
                if (!yearId) return;
                const res = await PeriodsService.GetPeriodsByAcademicYear?.({ AcademicYearId: yearId });
                if (res && Array.isArray(res)) this.periods = res;
            } catch {
                // opcional
            }
        },

        rebuildCourseGroups() {
            const yearId = Number(this.filters.AcademicYearId || 0);
            const hqId = Number(this.filters.HeadquarterId || 0);
            if (!yearId || !hqId) {
                this.courseGroups = [];
                return;
            }

            const courseIdsAtHq = new Set(
                (this.courses || []).filter((c: any) => Number(c.HeadquarterId) === hqId).map((c: any) => Number(c.Id))
            );

            this.courseGroups = (this.courseGroupsAll || [])
                .filter((cg: any) => Number(cg.AcademicYearId) === yearId && courseIdsAtHq.has(Number(cg.CourseId)))
                .sort((a: any, b: any) => (a.Name || "").localeCompare(b.Name || ""));
        },

        async loadEnrollments() {
            try {
                this.errors.filters = "";
                this.errors.preview = "";
                this.previewHtml = "";
                this.previewStudentId = 0;

                if (!this.canSearch) {
                    this.errors.filters = "Complete Año, Sede, Grupo y Período.";
                    return;
                }

                this.loading.enrollments = true;
                this.selectedStudentIds = [];

                const payload = {
                    AcademicYearId: Number(this.filters.AcademicYearId),
                    HeadquarterId: Number(this.filters.HeadquarterId),
                    GroupId: Number(this.filters.CourseGroupId)
                };

                const list = await IndividualEnrollmentService.GetEnrollmentsByGroupYear(payload);
                console.log("Matriculados recibidos:", list);

                this.enrollments = (list || []).map((x: any) => ({
                    StudentId: x.StudentId,
                    EnrollmentId: x.EnrollmentId,
                    StudentName: `${x.StudentName || ""}`.trim(),
                    IdentificationNumber: x.IdentificationNumber,
                    StudentEmail: x.StudentEmail || x.PersonalEmail || x.InstitucionalEmail,
                    ParentsEmail: x.ParentsEmail || x.ParentEmail || x.MotherEmail || x.FatherEmail,
                    GroupName: x.GroupName || x.CourseGroupName
                }));


            } catch (e: any) {
                store.dispatch("core/popupDialog", {
                    text: `Error cargando matriculados: ${e?.response?.data?.message || e?.message}`,
                    type: "error",
                    showCancelButton: false
                });
            } finally {
                this.loading.enrollments = false;
            }
        },

        selectAll(flag: boolean) {
            if (Number(this.filters.StudentId) > 0) return;
            this.selectedStudentIds = flag ? this.enrollments.map((e: any) => Number(e.StudentId)) : [];
        },

        getInitials(name: string) {
            const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
            const a = (parts[0] || "E").charAt(0).toUpperCase();
            const b = (parts[1] || "").charAt(0).toUpperCase();
            return (a + b).trim();
        },

        renderPreviewFrame(html: string) {
            const frame: any = this.$refs.previewFrame;
            if (!frame) return;

            const doc = frame.contentDocument || frame.contentWindow?.document;
            if (!doc) return;

            doc.open();
            doc.write(html || "<div style='padding:16px;font-family:system-ui'>Sin contenido</div>");
            doc.close();
        },

        async actionPrint(singleStudentId?: number) {
            try {
                this.loading.action = true;

                // Validación mínima
                if (!this.canSearch) {
                    store.dispatch("core/popupDialog", {
                        text: "Complete Año, Grupo y Período antes de imprimir.",
                        type: "warning",
                        showCancelButton: false
                    });
                    return;
                }

                // Resolver scope + payload
                const payload: any = {
                    output: "PRINT",
                    courseGroupId: Number(this.filters.CourseGroupId),
                    periodId: Number(this.filters.PeriodId),
                    // si lo necesitas (según tu backend):
                    academicYearId: Number(this.filters.AcademicYearId),

                    templateCode: "TMP-BOLETIN-PREESCOLAR",
                    combinePdfForPrint: true,
                    zipIfMultiple: true
                };

                if (singleStudentId) {
                    payload.scope = "STUDENT";
                    payload.studentId = Number(singleStudentId);
                } else if (Number(this.filters.StudentId) > 0) {
                    payload.scope = "STUDENT";
                    payload.studentId = Number(this.filters.StudentId);
                } else if (this.selectedStudentIds?.length) {
                    payload.scope = "SELECTION";
                    payload.studentIds = this.selectedStudentIds.map((x: any) => Number(x)).filter((x: number) => x > 0);
                } else {
                    payload.scope = "GROUP";
                }

                // ✅ Llamada única (devuelve PDF o ZIP)
                const res = await PreschoolReportsService.PrintPreschoolReport(payload);
                    
                // res debe ser un Blob (axios responseType: 'blob')
                const blob = res as Blob;
                const contentType = blob.type || "application/octet-stream";

                // Nombre sugerido
                const fileName =
                    contentType.includes("zip")
                        ? `BOLETIN_PRESCOLAR_GRUPO_${payload.courseGroupId}_P${payload.periodId}.zip`
                        : `BOLETIN_PRESCOLAR_GRUPO_${payload.courseGroupId}_P${payload.periodId}.pdf`;

                this.downloadBlob(blob, fileName);

            } catch (e: any) {
                store.dispatch("core/popupDialog", {
                    text: `Error imprimiendo: ${e?.response?.data?.message || e?.message}`,
                    type: "error",
                    showCancelButton: false
                });
            } finally {
                this.loading.action = false;
            }
        },

        downloadBlob(blob: Blob, fileName: string) {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        },



        mergeHtmlForPrint(htmlDocs: string[]) {
            const body = htmlDocs
                .map((h, i) => {
                    const bodyMatch = String(h).match(/<body[^>]*>([\s\S]*?)<\/body>/i);
                    const inner = bodyMatch ? bodyMatch[1] : h;
                    return `<div class="print-page">${inner}</div>${i < htmlDocs.length - 1 ? `<div style="page-break-after:always;"></div>` : ""}`;
                })
                .join("");

            const headMatch = String(htmlDocs[0] || "").match(/<head[^>]*>([\s\S]*?)<\/head>/i);
            const head = headMatch ? headMatch[1] : `<meta charset="utf-8" />`;

            return `<!DOCTYPE html><html><head>${head}</head><body>${body}</body></html>`;
        },

        printHtml(html: string) {
            const w = window.open("", "_blank");
            if (!w) return;

            w.document.open();
            w.document.write(html);
            w.document.close();

            setTimeout(() => {
                w.focus();
                w.print();
            }, 250);
        },

        async actionEmail(singleStudentId?: number) {
            try {
                if (this.filters.DeliveryMode !== "EMAIL") {
                    this.filters.DeliveryMode = "EMAIL";
                    return;
                }
                if (!this.canRunEmail) return;

               /* this.loading.action = true;

                const ids = singleStudentId ? [Number(singleStudentId)] : this.effectiveSelectedIds;

                const res = await PreschoolReportsService.SendPreschoolReportsEmail({
                    AcademicYearId: Number(this.filters.AcademicYearId),
                    CourseGroupId: Number(this.filters.CourseGroupId),
                    PeriodId: Number(this.filters.PeriodId),
                    StudentIds: ids,
                    SendToStudentEmail: !!this.emailOptions.SendToStudentEmail,
                    SendToParentsEmail: !!this.emailOptions.SendToParentsEmail
                });

                store.dispatch("core/popupDialog", {
                    text: res?.Message || `Correos enviados: ${ids.length}`,
                    type: "success",
                    showCancelButton: false
                });*/
            } catch (e: any) {
                store.dispatch("core/popupDialog", {
                    text: `Error enviando correos: ${e?.response?.data?.message || e?.message}`,
                    type: "error",
                    showCancelButton: false
                });
            } finally {
                this.loading.action = false;
            }
        },

        clearAll() {
            this.filters.AcademicYearId = 0 as any;
            this.filters.HeadquarterId = 0 as any;
            this.filters.CourseGroupId = 0 as any;
            this.filters.PeriodId = 0 as any;
            this.filters.StudentId = 0 as any;
            this.filters.DeliveryMode = "PRINT";

            this.enrollments = [];
            this.selectedStudentIds = [];
            this.previewHtml = "";
            this.previewStudentId = 0;
            this.errors.filters = "";
            this.errors.preview = "";
        }
    }
});
